
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useCompanyStore } from "@/lib/stores/companyStore";
import { Company } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { getStandardizedLocation } from "@/utils/locationHelpers";

export function useCompanyOperations(companyId?: string) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { companies } = useCompanyStore();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get updated company store methods each time we need them
  const updateCompany = useCompanyStore((state) => state.updateCompany);
  const fetchCompanies = useCompanyStore((state) => state.fetchCompanies);

  useEffect(() => {
    async function loadCompany() {
      if (!companyId) {
        setLoading(false);
        return;
      }
      
      // First try to find the company in the local state
      let foundCompany = companies.find(c => c.id === companyId);
      
      // If not found in local state, try to fetch it directly from Supabase
      if (!foundCompany) {
        try {
          // Fetch the specific company from Supabase
          const { data, error } = await supabase
            .from('companies')
            .select('*')
            .eq('id', companyId)
            .single();
            
          if (error) {
            console.error("Error fetching company from Supabase:", error);
            // Attempt to refresh the companies list
            await fetchCompanies();
            
            // Check again in the updated list
            foundCompany = companies.find(c => c.id === companyId);
          } else if (data) {
            // Convert Supabase data to our app's Company format
            foundCompany = {
              id: data.id,
              name: data.name,
              locations: data.locations || [],
              createdAt: new Date(data.created_at).getTime(),
              bio: data.bio || '',
              staffSize: data.staff_size || '',
              logo: data.logo || '',
              bannerImage: data.banner_image || '',
              website: data.website || '',
              twitterUrl: data.twitter_url || '',
              instagramUrl: data.instagram_url || '',
              linkedinUrl: data.linkedin_url || '',
              isVisible: data.is_visible
            };
          }
        } catch (error) {
          console.error("Error in loadCompany:", error);
        }
      }
      
      setCompany(foundCompany || null);
      setLoading(false);
    }
    
    loadCompany();
  }, [companyId, companies, fetchCompanies]);

  const handleUpdateCompany = async (data: { 
    name?: string; 
    location?: string; 
    bio?: string; 
    staffSize?: string; 
    logo?: File | string;
    bannerImage?: File | string;
    website?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    linkedinUrl?: string;
    isVisible?: boolean;
  }) => {
    if (!companyId || !company) return;
    
    // Ensure location is standardized
    const location = data.location ? getStandardizedLocation(data.location) : null;
    
    // Convert the form data to match what updateCompany expects
    const updatedCompany = {
      name: data.name || company.name,
      locations: location ? [location] : company.locations,
      bio: data.bio || company.bio || "",
      staffSize: data.staffSize || company.staffSize || "",
      logo: data.logo ? 
        (data.logo instanceof File ? URL.createObjectURL(data.logo) : data.logo) : 
        company.logo || "",
      bannerImage: data.bannerImage ? 
        (data.bannerImage instanceof File ? URL.createObjectURL(data.bannerImage) : data.bannerImage) : 
        company.bannerImage || "",
      website: data.website || company.website || "",
      twitterUrl: data.twitterUrl || company.twitterUrl || "",
      instagramUrl: data.instagramUrl || company.instagramUrl || "",
      linkedinUrl: data.linkedinUrl || company.linkedinUrl || "",
      isVisible: data.isVisible !== undefined ? data.isVisible : company.isVisible,
    };
    
    const success = await updateCompany(companyId, updatedCompany);
    
    if (success) {
      toast({
        title: "Company updated",
        description: `${updatedCompany.name} has been updated successfully`,
      });
      
      navigate("/admin/dashboard");
    } else {
      toast({
        title: "Update failed",
        description: "There was an error updating the company. Please try again.",
        variant: "destructive"
      });
    }
  };

  return {
    company,
    loading,
    updateCompany: handleUpdateCompany,
    navigateToDashboard: () => navigate("/admin/dashboard")
  };
}
